import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, Package } from 'lucide-react';
import { productAPI } from '../../services/api';

/**
 * Reusable searchable product picker with server-side search.
 * 
 * Props:
 * - value: string (product name or search text)
 * - productId: string (selected product ID, optional)
 * - onSelect: (product) => void — called when a product is selected from dropdown
 * - onChange: (text) => void — called on free text change (for manual entry)
 * - onClear: () => void — called when cleared
 * - placeholder: string
 * - disabled: boolean
 * - readOnly: boolean
 * - className: string (additional classes)
 * - testId: string (data-testid)
 * - showMeta: boolean — show SKU/category in dropdown (default true)
 * - autoFillFields: string[] — which fields to auto-fill on select (default all)
 */
const ProductSearchInput = ({
  value = '',
  productId = '',
  branchId = '',
  onSelect,
  onChange,
  onClear,
  placeholder = 'Search product by name or SKU...',
  disabled = false,
  readOnly = false,
  className = '',
  testId = 'product-search',
  showMeta = true,
}) => {
  const [search, setSearch] = useState(value);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState(!!productId);
  const containerRef = useRef();
  const inputRef = useRef();
  const debounceRef = useRef();
  const justSelectedRef = useRef(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Update dropdown position when showing
  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [showDropdown, results]);

  // Sync external value changes
  useEffect(() => {
    setSearch(value);
    setSelected(!!productId);
  }, [value, productId]);

  useEffect(() => {
    setResults([]);
    setShowDropdown(false);
  }, [branchId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced server search
  const doSearch = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    setLoading(true);
    try {
      const params = { search: query, page: 1, page_size: 30 };
      if (branchId) params.branch = branchId;
      const data = await productAPI.getAll(params);
      const items = Array.isArray(data) ? data : (data?.items || data || []);
      setResults(items);
      if (!justSelectedRef.current) {
        setShowDropdown(items.length > 0);
      }
    } catch {
      setResults([]);
    }
    setLoading(false);
  }, [branchId]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setSelected(false);
    justSelectedRef.current = false;
    onChange?.(val);

    // Debounce search
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (product) => {
    justSelectedRef.current = true;
    setSearch(product.name);
    setSelected(true);
    setShowDropdown(false);
    setResults([]);
    onSelect?.(product);
    // Ensure dropdown stays closed even after parent re-render
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 500);
  };

  const handleClear = () => {
    setSearch('');
    setSelected(false);
    setResults([]);
    setShowDropdown(false);
    justSelectedRef.current = false;
    onClear?.();
  };

  const handleFocus = () => {
    // Don't reopen dropdown right after selection
    if (justSelectedRef.current) return;
    if (results.length > 0 && !selected) {
      setShowDropdown(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          value={search}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          data-testid={testId}
          className={`w-full pl-7 pr-8 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 ${
            selected ? 'bg-blue-50/50 border-blue-200' : ''
          } ${disabled || readOnly ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''} ${className}`}
        />
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-gray-400" />
        )}
        {!loading && search && !disabled && !readOnly && (
          <button
            onClick={handleClear}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100"
            tabIndex={-1}
          >
            <X className="w-3 h-3 text-gray-400" />
          </button>
        )}
      </div>

      {/* Dropdown results — fixed positioning to avoid overflow clipping */}
      {showDropdown && results.length > 0 && (
        <div className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl max-h-[280px] overflow-y-auto"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width || 300 }}>
          {results.map((product) => (
            <div
              key={product.id}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(product); }}
              data-testid={`product-option-${product.id}`}
              className="w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0 cursor-pointer"
            >
              <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Package className="w-3 h-3 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">{product.name}</p>
                {showMeta && (
                  <div className="flex items-center gap-2 mt-0.5">
                    {product.sku && (
                      <span className="text-[9px] text-gray-400 font-mono">{product.sku}</span>
                    )}
                    {product.category && (
                      <span className="text-[9px] px-1 py-0.5 bg-gray-100 rounded text-gray-500">{product.category}</span>
                    )}
                    {product.unit && (
                      <span className="text-[9px] text-gray-400">{product.unit}</span>
                    )}
                    {product.hsn_code && (
                      <span className="text-[9px] text-gray-400">HSN: {product.hsn_code}</span>
                    )}
                    {typeof product.branch_available === 'number' && (
                      <span
                        className={`text-[9px] px-1 py-0.5 rounded font-semibold ${
                          product.branch_available > 0
                            ? 'bg-green-100 text-green-700'
                            : product.branch_available < 0
                              ? 'bg-red-100 text-red-600'
                              : 'bg-gray-100 text-gray-500'
                        }`}
                        title={`Physical: ${product.branch_stock ?? 0} | Available: ${product.branch_available}`}
                        data-testid={`product-branch-stock-${product.id}`}
                      >
                        {product.branch_available} {product.unit || ''} at branch
                      </span>
                    )}
                  </div>
                )}
              </div>
              {product.selling_price > 0 && (
                <span className="text-[10px] font-semibold text-gray-600 flex-shrink-0">
                  {product.selling_price.toLocaleString()}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showDropdown && results.length === 0 && search.length >= 2 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center">
          <p className="text-[10px] text-gray-500">No products found for &quot;{search}&quot;</p>
          <p className="text-[9px] text-gray-400 mt-0.5">You can type a custom name</p>
        </div>
      )}
    </div>
  );
};

export default ProductSearchInput;
